import { BaseEmailProvider } from '../BaseEmailProvider';
import type { EmailData, RegistrationNotificationData, EmailResponse } from '../types';

/**
 * SMTP Email Provider (via backend API)
 */
export class SMTPProvider extends BaseEmailProvider {
  protected providerName = 'SMTP';

  /**
   * Validate SMTP configuration
   */
  validateConfiguration(): boolean {
    const host = import.meta.env.VITE_SMTP_HOST;
    const port = import.meta.env.VITE_SMTP_PORT;
    const user = import.meta.env.VITE_SMTP_USER;
    const pass = import.meta.env.VITE_SMTP_PASS;
    const apiEndpoint = import.meta.env.VITE_EMAIL_API_ENDPOINT;

    // Check if we have either SMTP credentials or API endpoint
    const hasSmtpConfig = host && port && user && pass;
    const hasApiEndpoint = apiEndpoint;

    return hasSmtpConfig || hasApiEndpoint;
  }

  /**
   * Send contact email via backend SMTP API
   */
  async sendEmail(data: EmailData): Promise<EmailResponse> {
    try {
      this.logEmailAttempt(data, 'contact');

      if (!this.validateConfiguration()) {
        throw new Error('SMTP configuration is invalid. Check your SMTP settings or API endpoint.');
      }

      const emailContent = this.generateContactEmailContent(data);
      
      const result = await this.sendViaBackendAPI({
        from: emailContent.fromEmail,
        to: emailContent.toEmail,
        subject: emailContent.subject,
        html: emailContent.htmlContent,
        text: emailContent.textContent,
        replyTo: data.userEmail
      });

      this.logEmailSuccess(result.messageId, emailContent.toEmail);

      return {
        success: true,
        message: 'Email successfully sent to Fabrik 28',
        messageId: result.messageId
      };

    } catch (error: any) {
      this.logEmailError(error, data.to);
      
      return {
        success: false,
        message: `SMTP error: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Send registration notification via backend SMTP API
   */
  async sendRegistrationNotification(data: RegistrationNotificationData): Promise<EmailResponse> {
    try {
      this.logEmailAttempt(data, 'registration');

      if (!this.validateConfiguration()) {
        throw new Error('SMTP configuration is invalid. Check your SMTP settings or API endpoint.');
      }

      const emailContent = this.generateRegistrationEmailContent(data);
      
      const result = await this.sendViaBackendAPI({
        from: emailContent.fromEmail,
        to: emailContent.toEmail,
        subject: emailContent.subject,
        html: emailContent.htmlContent,
        text: emailContent.textContent,
        replyTo: data.userEmail
      });

      this.logEmailSuccess(result.messageId, emailContent.toEmail);

      return {
        success: true,
        message: 'Registration notification sent successfully via SMTP',
        messageId: result.messageId
      };

    } catch (error: any) {
      this.logEmailError(error, this.getToEmail());
      
      return {
        success: false,
        message: `SMTP error: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Send email via backend API
   */
  private async sendViaBackendAPI(emailData: {
    from: string;
    to: string;
    subject: string;
    html: string;
    text: string;
    replyTo: string;
  }): Promise<{ messageId: string }> {
    const apiEndpoint = import.meta.env.VITE_EMAIL_API_ENDPOINT || '/api/send-email';
    
    const payload = {
      from: emailData.from,
      to: emailData.to,
      subject: emailData.subject,
      html: emailData.html,
      text: emailData.text,
      replyTo: emailData.replyTo,
      // Include SMTP configuration for backend
      smtpConfig: {
        host: import.meta.env.VITE_SMTP_HOST,
        port: parseInt(import.meta.env.VITE_SMTP_PORT || '587'),
        user: import.meta.env.VITE_SMTP_USER,
        pass: import.meta.env.VITE_SMTP_PASS,
      }
    };
    
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Backend API error: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    
    return {
      messageId: result.messageId || result.id || 'unknown'
    };
  }
}
